import { useEffect, useState } from "react"

import { useParams } from "next/navigation"

import type { DeliveryEstimate } from "@/lib/helpers/delivery"
import type { CheckoutOrderSummary } from "@/lib/types/checkout-session"
import type { ProductPriceWire } from "@/lib/types/price"
import { getMarketplaceErrorCode, MarketplaceErrorCode } from "@/lib/utils/marketplace-errors"
import { PSEUDO_ID_COOKIE } from "@/platform/api/constants"
import { errorMessage } from "@/platform/api/errors"
import { catalogGateway } from "@/platform/api/gateways/catalog"
import type { StoreProduct, StoreProductVariant } from "@/platform/api/store-contract"
import { deleteCookie, readCookie, setCookie } from "@/platform/browser/cookies"
import { logger } from "@/platform/observability/logger"
import { getSessionCookie } from "@/platform/session/auth/get-session-cookie-client"
import { useGateway } from "@/platform/session/use-api"
import { usePrivateQuery } from "@/platform/session/use-private-query"
import { getBrowserCartProductDetails, getBrowserLastCheckoutProductDetails } from "@/platform/shopper/browser-product-pricing"

// Types
interface ProductTotalBreakdown {
  shippingTaxTotal?: number | null
  shippingTotal?: number
  taxTotal?: number
  subTotal?: number
  total?: number
  discountTotal?: number
  creditTotal?: number
}

interface ProductDetails {
  thumbnail: string | null
  name: string
}

export interface CartDetails {
  productTotalBreakdown: ProductTotalBreakdown
  total: number
  productDetails: ProductDetails
}

export const toCartDetails = (orderSummary: CheckoutOrderSummary | null): CartDetails | null => {
  if (!orderSummary) return null

  return {
    productTotalBreakdown: {
      shippingTaxTotal: null,
      shippingTotal: orderSummary.shippingTotal,
      taxTotal: orderSummary.taxTotal,
      subTotal: orderSummary.subtotal,
      total: orderSummary.total,
      discountTotal: orderSummary.discountTotal,
      creditTotal: orderSummary.appliedCredit?.amount ?? 0,
    },
    total: orderSummary.total,
    productDetails: {
      thumbnail: orderSummary.primaryItem?.thumbnail ?? null,
      name: orderSummary.primaryItem?.title ?? "",
    },
  }
}

const ERROR_MESSAGES = {
  ADDRESS_UPDATE_FAILED: "Error al actualizar la dirección. Intenta de nuevo.",
  PAYMENT_SESSION_FAILED: "Error al procesar el pago. Intenta de nuevo.",
  CART_COMPLETE_FAILED: "Error al finalizar la compra. Intenta de nuevo.",
  LOAN_OPTIONS_FAILED: "No se pudieron obtener opciones de pago.",
  NETWORK_ERROR: "Error de conexión. Verifica tu internet e intenta de nuevo.",
  UNKNOWN_ERROR: "Ocurrió un error inesperado. Intenta de nuevo.",
} as const

export const mapErrorToMessage = (error: Error | null, fallbackMessage: string = ERROR_MESSAGES.UNKNOWN_ERROR): string => {
  if (!error) return fallbackMessage

  if (error instanceof TypeError) return ERROR_MESSAGES.NETWORK_ERROR

  switch (getMarketplaceErrorCode(error)) {
    case MarketplaceErrorCode.CheckoutMissingAddress:
      return ERROR_MESSAGES.ADDRESS_UPDATE_FAILED
    case MarketplaceErrorCode.CheckoutPaymentSessionRequired:
    case MarketplaceErrorCode.PaymentSessionMissingData:
    case MarketplaceErrorCode.PaymentAmountMismatch:
    case MarketplaceErrorCode.PaymentAuthorizationInvalid:
    case MarketplaceErrorCode.PaymentAuthorizationExpired:
    case MarketplaceErrorCode.PaymentIntentInvalid:
    case MarketplaceErrorCode.PaymentIntentExpired:
      return ERROR_MESSAGES.PAYMENT_SESSION_FAILED
    case MarketplaceErrorCode.LoanAlreadyExists:
    case MarketplaceErrorCode.LoanInvalidState:
    case MarketplaceErrorCode.LoanCreditLimitExceeded:
      return ERROR_MESSAGES.LOAN_OPTIONS_FAILED
    default:
      return errorMessage(error, fallbackMessage)
  }
}

const COOKIE_NAME = "last_checkout_product"
// Custom event for cookie changes
const COOKIE_CHANGE_EVENT = "last_checkout_product_change"

interface LastCheckoutProductRef {
  id: string
  variantId: string
  timestamp: string
}

/**
 * Display data for the last checkout product, fetched fresh and priced on each
 * cart view from {@link LastCheckoutProductRef}.
 */
export interface LastCheckoutProduct {
  id: string
  title: string
  handle: string
  price: string
  /** Pre-markdown price, present only when a discount actually applies. */
  originalPrice?: string
  variantId: string
  variantLabel?: string
  thumbnail?: string
  inStock: boolean
  freeShipping?: boolean
  delivery?: DeliveryEstimate
  resolvedPrice?: ProductPriceWire | null
}

export type LastCheckoutDetailsResult = { status: "ok"; product: LastCheckoutProduct } | { status: "unavailable" } | { status: "error" }

/**
 * Saves a pointer to the last product that a user attempted to checkout with.
 *
 * @param options - Product and variant to remember
 */
export const saveLastCheckoutProduct = (options: { product: StoreProduct; variant: StoreProductVariant }): void => {
  try {
    const { product, variant } = options

    if (!product?.id) throw new Error("Product ID is required")
    if (!variant?.id) throw new Error("Variant ID is required")

    const ref: LastCheckoutProductRef = {
      id: product.id,
      variantId: variant.id,
      timestamp: new Date().toISOString(),
    }

    const maxAge = 6 * 60 * 60 // 6 hours

    setCookie(COOKIE_NAME, JSON.stringify(ref), { maxAgeSeconds: maxAge, sameSite: "Strict" })

    // Dispatch event to notify listeners that the cookie has changed
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(COOKIE_CHANGE_EVENT, { detail: ref }))
    }
  } catch {
    // no-op
  }
}

/**
 * Validates that the parsed data has the expected structure for a {@link LastCheckoutProductRef}.
 *
 * @param data - The parsed data to validate
 * @returns True if the data is valid, false otherwise
 */
const validateLastCheckoutProductRef = (data: unknown): data is LastCheckoutProductRef => {
  if (typeof data !== "object" || data === null) {
    return false
  }

  const ref = data as Record<string, unknown>

  const requiredStringFields: (keyof LastCheckoutProductRef)[] = ["id", "variantId", "timestamp"]
  for (const field of requiredStringFields) {
    if (typeof ref[field] !== "string") {
      logger.error("Invalid or missing field in last checkout product", { field, value: ref[field] })
      return false
    }
  }

  return true
}

/**
 * Retrieves the pointer to the last product that a user attempted to checkout with.
 *
 * @returns The last checkout product ref or null if not found or invalid
 */
export const getLastCheckoutProductRef = (): LastCheckoutProductRef | null => {
  try {
    const cookieValue = readCookie(COOKIE_NAME)
    if (cookieValue) {
      const parsedData = JSON.parse(cookieValue)

      if (validateLastCheckoutProductRef(parsedData)) {
        return { id: parsedData.id, variantId: parsedData.variantId, timestamp: parsedData.timestamp }
      }

      logger.error("Invalid data structure in last checkout product cookie", { parsedData })
    }
  } catch {
    // no-op
  }
  return null
}

export const refreshLastCheckoutProductRef = (): LastCheckoutProductRef | null => {
  const ref = getLastCheckoutProductRef()
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(COOKIE_CHANGE_EVENT, { detail: ref }))
  return ref
}

/**
 * Removes the last checkout product cookie
 */
export const clearLastCheckoutProduct = (): void => {
  try {
    deleteCookie(COOKIE_NAME, { sameSite: "Strict" })

    // Dispatch event to notify listeners that the cookie has been cleared
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(COOKIE_CHANGE_EVENT, { detail: null }))
    }
  } catch {
    // no-op
  }
}

/**
 * Hook to watch for changes to the last checkout product cookie
 *
 * @returns The last checkout product ref or null if not found or invalid
 */
export const useLastCheckoutProductRef = (): LastCheckoutProductRef | null => {
  // Initialize to null on both the server and the first client render to avoid a
  // hydration mismatch; the real cookie value is read on mount in the effect.
  const [lastProduct, setLastProduct] = useState<LastCheckoutProductRef | null>(null)

  useEffect(() => {
    // Read the current cookie value on mount (client-only).
    setLastProduct(getLastCheckoutProductRef())

    // Handler for cookie change events
    const handleCookieChange = (event: CustomEvent<LastCheckoutProductRef | null>) => {
      if (event && event.detail) {
        // Only validate if there is data in event.detail
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        validateLastCheckoutProductRef(event.detail) ? setLastProduct(event.detail) : setLastProduct(null)
      } else {
        // If event.detail is null (cookie was cleared), set lastProduct to null
        setLastProduct(null)
      }
    }

    window.addEventListener(COOKIE_CHANGE_EVENT, handleCookieChange as EventListener)
    return () => {
      window.removeEventListener(COOKIE_CHANGE_EVENT, handleCookieChange as EventListener)
    }
  }, [])

  return lastProduct
}

/**
 * Reads the last checkout product ref and fetches its current product details
 * (price, title, thumbnail) fresh on each view, so the cart never shows a stale
 * price that disagrees with the product page. Backed by React Query: caching,
 * request dedup (the cart page and the dropdown share one query key, so they hit
 * the backend once), retries, and loading/error states come from the query cache.
 *
 * pseudoId is part of the key because the price embeds personalized discounts
 * keyed off that cookie — otherwise a cached price could leak across identities.
 *
 * @param enabled - When false, no fetch runs (used to defer the dropdown's fetch
 *   until it is opened); cached data is still returned instantly on re-enable.
 */
export const useLastCheckoutProductDetails = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const ref = useLastCheckoutProductRef()
  const params = useParams<{ countryCode?: string }>()
  const countryCode = typeof params?.countryCode === "string" ? params.countryCode : ""
  const pseudoId = getSessionCookie(PSEUDO_ID_COOKIE) ?? ""
  const catalog = useGateway(catalogGateway)

  const query = usePrivateQuery(
    {
      queryKey: ["lastCheckoutProductDetails", ref?.id, ref?.variantId, countryCode, pseudoId],
      queryFn: async () => {
        const res = await getBrowserLastCheckoutProductDetails(catalog, { id: ref!.id, variantId: ref!.variantId, countryCode })
        // Surface transient failures as query errors so they aren't cached and stay retryable.
        if (res.status === "error") throw new Error("Failed to load last checkout product")
        return res
      },
      enabled: enabled && !!ref,
      staleTime: 1000 * 60,
      retry: 1,
    },
    true
  )

  const result = query.data ?? null

  // A genuinely-gone variant drops the pointer here (the shared hook), so every
  // surface agrees: the cart page, the dropdown, and the notification badge.
  useEffect(() => {
    if (result?.status === "unavailable") clearLastCheckoutProduct()
  }, [result])

  // isFetching (not just isLoading) so a retry after an error shows progress —
  // isLoading is only true on the first fetch with no prior data.
  return { ref, result, isLoading: query.isLoading || query.isFetching, isError: query.isError, refetch: query.refetch }
}

/**
 * Fetches the current product details (price, title, thumbnail) of a cart line
 * fresh on each view, so the cart never shows a stale price that disagrees with
 * the product page. Backed by React Query: caching, request dedup, retries, and
 * loading/error states come from the query cache.
 *
 * pseudoId is part of the key because the price embeds personalized discounts
 * keyed off that cookie — otherwise a cached price could leak across identities.
 */
export const useCartProductDetails = (item: { productId: string; variantId: string } | null) => {
  const params = useParams<{ countryCode?: string }>()
  const countryCode = typeof params?.countryCode === "string" ? params.countryCode : ""
  const pseudoId = getSessionCookie(PSEUDO_ID_COOKIE) ?? ""
  const catalog = useGateway(catalogGateway)

  const query = usePrivateQuery({
    queryKey: ["cartProductDetails", item?.productId, item?.variantId, countryCode, pseudoId],
    queryFn: async () => {
      if (!item) throw new Error("Cart product requires an item")
      return getBrowserCartProductDetails(catalog, { id: item.productId, variantId: item.variantId, countryCode })
    },
    enabled: !!item,
    staleTime: 1000 * 60,
    retry: 1,
  })

  return {
    product: query.data,
    isUnavailable: query.isSuccess && query.data === null,
    isLoading: query.isLoading || query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  }
}
